import Cookies from 'js-cookie';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { GlobalContext, isAvMed } from '../../context';
import {
  IDLE_SURVEY_DISMISSED_KEY,
  IDLE_SURVEY_BASE_URL,
  ACTIVITY_EVENTS,
  isInputFocused,
  hasBlockingModal,
  isChatbotVisible,
  isCoBrowseActive,
  isExcludedPage,
  mapLobCode,
  mapCarrierCode,
  mapDeviceTypeCode,
  resolveIdleTimeout,
} from '../utils/idleSurveyUtils';
import { getDecryptedValue } from '../utils/LocalStorageUtils';
import { safeJsonParse } from '../utils/ClaimUtils';
import { UserDetails } from '../utils/constants/Constants';
import { useDeviceType } from '../utils/DeviceTypeProvider';
import { encryptValue } from '../utils/cryptoUtils';

const getDismissedCookieKey = async (personId: string): Promise<string> => {
  if (!personId) return IDLE_SURVEY_DISMISSED_KEY;
  const encryptedId = await encryptValue(personId);
  return encryptedId
    ? `${IDLE_SURVEY_DISMISSED_KEY}_${encryptedId}`
    : IDLE_SURVEY_DISMISSED_KEY;
};

/**
 * Check if the idle survey modal has been dismissed for a specific user.
 * @param personId - The user's person ID
 * @returns Promise<boolean> - True if the user has dismissed the modal, false otherwise
 */
export const isIdleSurveyDismissed = async (personId: string): Promise<boolean> => {
  const cookieKey = await getDismissedCookieKey(personId);
  return Boolean(Cookies.get(cookieKey));
};

export type UseIdleSurveyModalResult = {
  isOpen: boolean;
  closeModal: () => void;
  surveyUrl: string;
};
 
export const useIdleSurveyModal = (): UseIdleSurveyModalResult => {
  const { getAuthToken, age, getFeature, accountType } =
    useContext(GlobalContext);
  const isLoggedIn = Boolean(getAuthToken);
  const isCSATEnabled = Boolean(getFeature?.CSATSurvey);
  const deviceType = useDeviceType();
  const location = useLocation();
  const [cookieKey, setCookieKey] = useState<string>('');
  const [isInitialized, setIsInitialized] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [surveyPersonId, setSurveyPersonId] = useState<string>('');
 
  const timerRef = useRef<ReturnType<typeof globalThis.setTimeout> | null>(null);
 
  // Initialize encrypted cookie key and check dismissal status
  useEffect(() => {
    let isCancelled = false;
    
    setIsInitialized(false);
    (async () => {
      const storedUserId = String(
        safeJsonParse(await getDecryptedValue(UserDetails.id), '')
      ).trim();
      const key = await getDismissedCookieKey(storedUserId);
      if (isCancelled) {
        return;
      }

      setSurveyPersonId(storedUserId);
      setCookieKey(key);
      setDismissed(Boolean(Cookies.get(key)));
      setIsInitialized(true);
    })();

    return () => {
      isCancelled = true;
    };
  }, [UserDetails.id]);
 
  const clearIdleTimer = useCallback(() => {
    if (timerRef.current !== null) {
      globalThis.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);
 
  const isEligible = useCallback(() => {
    return (
      isInitialized &&
      isLoggedIn &&
      isCSATEnabled &&
      !isInputFocused() &&
      !dismissed &&
      !hasBlockingModal() &&
      !isChatbotVisible() &&
      !isCoBrowseActive() &&
      !isExcludedPage(location.pathname) &&
      document.visibilityState === 'visible'
    );
  }, [dismissed, isCSATEnabled, isInitialized, isLoggedIn, location.pathname]);
 
  const openModal = useCallback(() => {
    if (!isEligible()) {
      return;
    }

    setIsOpen(true);
    Cookies.set(cookieKey || IDLE_SURVEY_DISMISSED_KEY, 'true', { expires: 60 });
  }, [isEligible]);
 
  const resetIdleTimer = useCallback(() => {
    clearIdleTimer();
 
    if (!isEligible()) {
      return;
    }
    
    timerRef.current = globalThis.setTimeout(() => {
      openModal();
    }, resolveIdleTimeout(getFeature));
  }, [clearIdleTimer, getFeature, openModal]);

  const surveyUrl = useMemo(() => {
    if (!IDLE_SURVEY_BASE_URL) return '';

    const params = new URLSearchParams({
      personID: surveyPersonId ,
      age: age ?? '',
      LOB: mapLobCode(accountType),
      carrier: mapCarrierCode(isAvMed()),
      pagename: location.pathname,
      deviceType: mapDeviceTypeCode(deviceType),
    });
    return `${IDLE_SURVEY_BASE_URL}&${params.toString()}`;
  }, [surveyPersonId, age, accountType, deviceType, location.pathname]);

 
  const closeModal = useCallback(() => {
    setDismissed(true);
    setIsOpen(false);
    clearIdleTimer();
  }, [clearIdleTimer, cookieKey]);
 
  // Start/stop timer based on eligibility.
  useEffect(() => {
    if (isEligible()) {
      resetIdleTimer();
    } else {
      clearIdleTimer();
      setIsOpen(false);
    }
 
    return clearIdleTimer;
  }, [isEligible, resetIdleTimer, clearIdleTimer]);
 
  // Listen for user activity.
  useEffect(() => {
    const handleActivity = () => {
      if (!isOpen) {
        resetIdleTimer();
      }
    };
 
    ACTIVITY_EVENTS.forEach((event) => {
      globalThis.addEventListener(event, handleActivity, {
        passive: true,
      });
    });
 
    return () => {
      ACTIVITY_EVENTS.forEach((event) => {
        globalThis.removeEventListener(event, handleActivity);
      });
    };
  }, [isOpen, resetIdleTimer]);
 
  // Pause/resume timer when tab visibility changes.
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        resetIdleTimer();
      } else {
        clearIdleTimer();
      }
    };
 
    document.addEventListener(
      'visibilitychange',
      handleVisibilityChange
    );
 
    return () => {
      document.removeEventListener(
        'visibilitychange',
        handleVisibilityChange
      );
    };
  }, [clearIdleTimer, resetIdleTimer]);
 
  return {
    isOpen,
    closeModal,
    surveyUrl,
  };
};